// Extracted from the web lap-brief dependency graph. Shared by API and web.
import { type MyChronSessionExport } from './telemetrySession.js';
import { type LapExclusion } from './telemetrySession.js';
import { type MeasurementPreferences } from '../../index.js';
import { type DetailedCircuitGeometry } from '../../index.js';
import { type CornerApexSuggestion } from './trackEnrichment.js';
import { type TrackContributionCornerApex } from '../../index.js';
import { analyzeLap } from './telemetrySession.js';
import { gpsPointsInWindow } from './telemetrySession.js';
import { type GpsCoord } from './trackEnrichment.js';
import { speedKphAtTime } from './telemetrySession.js';
import { type CurveType } from './trackEnrichment.js';
import { withDerivedGeometricApexes } from './detailedCircuitGeometry.js';
import { cornerApexSuggestionsFromDetailedGeometry } from './detailedCircuitGeometry.js';
import { detectCornersFromPolyline } from './trackEnrichment.js';
import { type MyChronGpsPoint } from './telemetrySession.js';
import { matchDetailedTurnPhases } from './turnPhaseTelemetry.js';
import { type TurnPhaseMatch } from './turnPhaseTelemetry.js';
import { smoothedSpeedKphAtTime } from './turnPhaseTelemetry.js';
import { seriesValueAtTime } from './telemetrySession.js';
import { TURN_TRAVERSE_MAX_DISTANCE_M } from './turnPhaseTelemetry.js';
import { buildCornerTimeWindows } from '../cornerWindows.js';
import { CURVE_TYPE_LABELS } from './trackEnrichment.js';
const BRIEF_VERSION = 2 as const;

const SCAN_STEP_MS = 40;

const BRAKE_CHANNEL_CANDIDATES = ['WBrk', 'BrkP', 'PBrk', 'Brk', 'Brake', 'Pedal'];

export type LapCornerSegmentBrief = {
  turnNumber: number;
  label: string;
  curveType: string;
  windowStartSec: number;
  windowEndSec: number;
  durationSec: number;
  entrySpeedKph: number | null;
  apexSpeedKph: number | null;
  exitSpeedKph: number | null;
  speedDropKph: number | null;
  maxLateralG: number | null;
  peakBrakeG: number | null;
  maxSteeringDeg: number | null;
  maxRpm: number | null;
  brakePressurePeak: number | null;
  brakeChannel: string | null;
};

export type LapTelemetryBrief = {
  version: typeof BRIEF_VERSION;
  session: {
    venue: string | null;
    driver: string | null;
    vehicle: string | null;
    logDate: string | null;
    logTime: string | null;
  };
  lapNumber: number;
  lapTimeSec: number;
  rank: number;
  totalLaps: number;
  gapToBestSec: number | null;
  topSpeedKph: number | null;
  averageSpeedKph: number | null;
  cornerCount: number;
  channelsAvailable: string[];
  corners: LapCornerSegmentBrief[];
  notes: string[];
};

export type LapTelemetryBriefOptions = {
  detailedGeometry?: DetailedCircuitGeometry | null;
  /** Circuit corner markers used when geometric phase matching is unavailable. */
  circuitCorners?: Array<CornerApexSuggestion | TrackContributionCornerApex>;
};

function round(value: number | null, decimals: number): number | null {
  if (value === null || !Number.isFinite(value)) {
    return null;
  }

  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function haversineMeters(a: GpsCoord, b: GpsCoord) {
  const earthRadiusM = 6_371_000;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  return 2 * earthRadiusM * Math.asin(Math.min(1, Math.sqrt(h)));
}

function nearestGpsPoint(gps: MyChronGpsPoint[], coord: GpsCoord): MyChronGpsPoint | null {
  if (gps.length === 0) {
    return null;
  }

  let best = gps[0]!;
  let bestDistance = haversineMeters(best, coord);

  for (const point of gps) {
    const distance = haversineMeters(point, coord);
    if (distance < bestDistance) {
      best = point;
      bestDistance = distance;
    }
  }

  return best;
}

function findBrakeChannel(session: MyChronSessionExport): string | null {
  for (const shortName of BRAKE_CHANNEL_CANDIDATES) {
    if (session.series[shortName]?.length) {
      return shortName;
    }
  }

  return null;
}

function listPopulatedChannels(session: MyChronSessionExport): string[] {
  return Object.entries(session.series)
    .filter(([, samples]) => samples.length > 0)
    .map(([shortName]) => shortName)
    .sort((left, right) => left.localeCompare(right));
}

function curveTypeLabel(curveType: CurveType | undefined) {
  if (!curveType) {
    return CURVE_TYPE_LABELS.unknown;
  }

  return CURVE_TYPE_LABELS[curveType] ?? CURVE_TYPE_LABELS.unknown;
}

function circuitCornerToApexSuggestion(corner: TrackContributionCornerApex): CornerApexSuggestion {
  return {
    id: corner.id,
    label: corner.label || `T${corner.turnNumber}`,
    lat: corner.lat,
    lon: corner.lon,
    turnNumber: corner.turnNumber,
    confidence: corner.confidence,
    curveType: corner.curveType as CurveType | undefined,
    detectionProfile: corner.detectionProfile as CornerApexSuggestion['detectionProfile'],
  };
}

function analyzeCornerWindow(
  session: MyChronSessionExport,
  lapStartMs: number,
  lapEndMs: number,
  startMs: number,
  endMs: number,
  apexMs: number,
  accXBaseline: number | null,
  brakeChannel: string | null,
  phase?: TurnPhaseMatch,
): Omit<LapCornerSegmentBrief, 'turnNumber' | 'label' | 'curveType'> {
  const entrySpeedKph = phase
    ? smoothedSpeedKphAtTime(session, phase.entryMs)
    : speedKphAtTime(session, startMs);
  const exitSpeedKph = phase
    ? smoothedSpeedKphAtTime(session, phase.exitMs)
    : speedKphAtTime(session, endMs);
  const apexSpeedKph = phase
    ? smoothedSpeedKphAtTime(session, phase.apexMs)
    : speedKphAtTime(session, apexMs);

  let maxLateralG = 0;
  let peakBrakeG = 0;
  let maxSteeringDeg = 0;
  let maxRpm = 0;
  let brakePressurePeak: number | null = null;
  let minSpeedInWindow: number | null = apexSpeedKph;

  const scanRanges = phase?.wrapsLap
    ? [
        [startMs, lapEndMs],
        [lapStartMs, endMs],
      ]
    : [[startMs, endMs]];
  for (const [rangeStart, rangeEnd] of scanRanges) {
    for (let timecodeMs = rangeStart!; timecodeMs <= rangeEnd!; timecodeMs += SCAN_STEP_MS) {
      const lateralG = seriesValueAtTime(session, 'AccY', timecodeMs, accXBaseline);
      if (lateralG !== null) {
        maxLateralG = Math.max(maxLateralG, Math.abs(lateralG));
      }

      const longitudinalG = seriesValueAtTime(session, 'AccX', timecodeMs, accXBaseline);
      if (longitudinalG !== null && longitudinalG < peakBrakeG) {
        peakBrakeG = longitudinalG;
      }

      const steeringDeg = seriesValueAtTime(session, 'StrA', timecodeMs, accXBaseline);
      if (steeringDeg !== null) {
        maxSteeringDeg = Math.max(maxSteeringDeg, Math.abs(steeringDeg));
      }

      const rpm = seriesValueAtTime(session, 'RPM', timecodeMs, accXBaseline);
      if (rpm !== null) {
        maxRpm = Math.max(maxRpm, rpm);
      }

      if (brakeChannel) {
        const brakeValue = seriesValueAtTime(session, brakeChannel, timecodeMs, accXBaseline);
        if (brakeValue !== null) {
          brakePressurePeak =
            brakePressurePeak === null ? brakeValue : Math.max(brakePressurePeak, brakeValue);
        }
      }

      const speedKph = speedKphAtTime(session, timecodeMs);
      if (speedKph !== null && (minSpeedInWindow === null || speedKph < minSpeedInWindow)) {
        minSpeedInWindow = speedKph;
      }
    }
  }

  const resolvedApexSpeed = phase ? apexSpeedKph : (minSpeedInWindow ?? apexSpeedKph);
  const effectiveEndMs = phase?.wrapsLap ? lapEndMs + (endMs - lapStartMs) : endMs;

  return {
    windowStartSec: round((startMs - lapStartMs) / 1000, 3)!,
    windowEndSec: round((effectiveEndMs - lapStartMs) / 1000, 3)!,
    durationSec: round((effectiveEndMs - startMs) / 1000, 3)!,
    entrySpeedKph: round(entrySpeedKph, 1),
    apexSpeedKph: round(resolvedApexSpeed, 1),
    exitSpeedKph: round(exitSpeedKph, 1),
    speedDropKph:
      entrySpeedKph !== null && resolvedApexSpeed !== null
        ? round(entrySpeedKph - resolvedApexSpeed, 1)
        : null,
    maxLateralG: round(maxLateralG, 2),
    peakBrakeG: peakBrakeG < -0.08 ? round(peakBrakeG, 2) : null,
    maxSteeringDeg: round(maxSteeringDeg, 0),
    maxRpm: round(maxRpm, 0),
    brakePressurePeak: round(brakePressurePeak, 1),
    brakeChannel,
  };
}

function buildCornerSegments(
  session: MyChronSessionExport,
  lapStartMs: number,
  lapEndMs: number,
  lapGps: MyChronGpsPoint[],
  corners: CornerApexSuggestion[],
  accXBaseline: number | null,
  brakeChannel: string | null,
): LapCornerSegmentBrief[] {
  const orderedCorners = corners
    .map((corner) => {
      const nearest = nearestGpsPoint(lapGps, { lat: corner.lat, lon: corner.lon });
      if (!nearest) {
        return null;
      }
      const distanceM = haversineMeters(nearest, { lat: corner.lat, lon: corner.lon });
      if (distanceM > TURN_TRAVERSE_MAX_DISTANCE_M) {
        return null;
      }
      return {
        corner,
        apexMs: nearest.timecodeMs,
      };
    })
    .filter((entry): entry is { corner: CornerApexSuggestion; apexMs: number } => entry !== null)
    .sort((left, right) => left.apexMs - right.apexMs);

  const windows = buildCornerTimeWindows(
    lapStartMs,
    lapEndMs,
    orderedCorners.map((entry) => entry.apexMs),
  );

  return orderedCorners.map(({ corner }, index) => {
    const window = windows[index]!;
    const metrics = analyzeCornerWindow(
      session,
      lapStartMs,
      lapEndMs,
      window.startMs,
      window.endMs,
      window.apexMs,
      accXBaseline,
      brakeChannel,
    );

    return {
      turnNumber: corner.turnNumber,
      label: corner.label,
      curveType: curveTypeLabel(corner.curveType),
      ...metrics,
    };
  });
}

function buildDetailedCornerSegments(
  session: MyChronSessionExport,
  lapStartMs: number,
  lapEndMs: number,
  lapGps: MyChronGpsPoint[],
  geometry: DetailedCircuitGeometry,
  accXBaseline: number | null,
  brakeChannel: string | null,
) {
  return matchDetailedTurnPhases(lapGps, geometry).map((phase) => ({
    turnNumber: phase.turnNumber,
    label: phase.label,
    curveType: 'Geometric circuit corner',
    ...analyzeCornerWindow(
      session,
      lapStartMs,
      lapEndMs,
      phase.entryMs,
      phase.exitMs,
      phase.apexMs,
      accXBaseline,
      brakeChannel,
      phase,
    ),
  }));
}

export function buildLapTelemetryBrief(
  session: MyChronSessionExport,
  lapNumber: number,
  exclusions: LapExclusion[] = [],
  _preferences: MeasurementPreferences = {
    speedUnit: 'KPH',
    temperatureUnit: 'C',
    distanceUnit: 'KM',
  },
  options: LapTelemetryBriefOptions = {},
): LapTelemetryBrief | null {
  const analysis = analyzeLap(session, lapNumber, exclusions);
  if (!analysis) {
    return null;
  }

  const { startMs, endMs } = analysis;
  const lapGps = gpsPointsInWindow(session.gps, startMs, endMs);
  const polyline: GpsCoord[] = lapGps.map((point) => ({ lat: point.lat, lon: point.lon }));
  const speedByIndex = lapGps.map((point) => {
    const kph = speedKphAtTime(session, point.timecodeMs);
    return kph ?? point.speed * 3.6;
  });
  const sessionCircuitCorners =
    session.metadata?.circuit?.cornerApexSuggestions
      ?.slice()
      .sort((left, right) => left.turnNumber - right.turnNumber)
      .map(circuitCornerToApexSuggestion) ?? [];
  const enrichedDetailedGeometry = options.detailedGeometry
    ? withDerivedGeometricApexes(options.detailedGeometry)
    : null;
  const geometryCircuitCorners = enrichedDetailedGeometry
    ? cornerApexSuggestionsFromDetailedGeometry(enrichedDetailedGeometry)
    : [];
  const suppliedCircuitCorners = (options.circuitCorners ?? []).map((corner) =>
    circuitCornerToApexSuggestion(corner as TrackContributionCornerApex),
  );
  const circuitCornerCandidates =
    suppliedCircuitCorners.length > 0
      ? suppliedCircuitCorners
      : sessionCircuitCorners.length > 0
        ? sessionCircuitCorners
        : geometryCircuitCorners;
  const corners =
    circuitCornerCandidates.length > 0
      ? circuitCornerCandidates
      : detectCornersFromPolyline(polyline, speedByIndex);
  const brakeChannel = findBrakeChannel(session);
  const detailedCorners = enrichedDetailedGeometry
    ? buildDetailedCornerSegments(
        session,
        startMs,
        endMs,
        lapGps,
        enrichedDetailedGeometry,
        analysis.accXBaseline,
        brakeChannel,
      )
    : [];
  const notes: string[] = [];

  if (lapGps.length < 12) {
    notes.push('Insufficient GPS samples on this lap to detect corners reliably.');
  } else if (detailedCorners.length === 0 && corners.length === 0) {
    notes.push('No corners were detected on this lap GPS trace.');
  }

  if (!session.series.AccX?.length || !session.series.AccY?.length) {
    notes.push(
      'Longitudinal/lateral G channels were missing; braking and cornering G peaks may be incomplete.',
    );
  }

  if (!brakeChannel) {
    notes.push(
      'No brake pressure channel found; peak braking is inferred from longitudinal G and speed drop.',
    );
  }

  return {
    version: BRIEF_VERSION,
    session: {
      venue: session.metadata?.venue ?? null,
      driver: session.metadata?.driver ?? null,
      vehicle: session.metadata?.vehicle ?? null,
      logDate: session.metadata?.logDate ?? null,
      logTime: session.metadata?.logTime ?? null,
    },
    lapNumber: analysis.lap.lapNumber,
    lapTimeSec: round(analysis.durationSec, 3)!,
    rank: analysis.rank,
    totalLaps: analysis.totalLaps,
    gapToBestSec: analysis.gapToBestSec === null ? null : round(analysis.gapToBestSec, 3),
    topSpeedKph: round(analysis.topSpeedKph, 1),
    averageSpeedKph: round(analysis.averageSpeedKph, 1),
    cornerCount: detailedCorners.length || corners.length,
    channelsAvailable: listPopulatedChannels(session),
    corners: detailedCorners.length
      ? detailedCorners
      : buildCornerSegments(
          session,
          startMs,
          endMs,
          lapGps,
          corners,
          analysis.accXBaseline,
          brakeChannel,
        ),
    notes,
  };
}
